import { useEffect, useState, useRef } from "react";



  const TICKERS = [
    { label: "BTC/USDT",         symbol: "BTC/USDT",         fetchUrl: "https://api.binance.com/api/v3/ticker/24hr?symbol=BTCUSDT" },
    { label: "ETH/USDT",         symbol: "ETH/USDT",         fetchUrl: "https://api.binance.com/api/v3/ticker/24hr?symbol=ETHUSDT" },
    { label: "BNB/USDT",         symbol: "BNB/USDT",         fetchUrl: "https://api.binance.com/api/v3/ticker/24hr?symbol=BNBUSDT" },
    { label: "XRP/USDT",         symbol: "XRP/USDT",         fetchUrl: "https://api.binance.com/api/v3/ticker/24hr?symbol=XRPUSDT" },
    { label: "MSTR",             symbol: "MSTR",             fetchUrl: "" },
    { label: "TSLA",             symbol: "TSLA",             fetchUrl: "" },
    { label: "TLT",              symbol: "TLT",              fetchUrl: "" },
    { label: "VIX",              symbol: "VIX",              fetchUrl: "" },
    { label: "ASST",             symbol: "ASST",             fetchUrl: "" },
    { label: "SATA",             symbol: "SATA",             fetchUrl: "" },
    { label: "STRC",             symbol: "STRC",             fetchUrl: "" },
    { label: "SOL/USDT",         symbol: "SOL/USDT",         fetchUrl: "https://api.binance.com/api/v3/ticker/24hr?symbol=SOLUSDT" },
    { label: "SPCX",             symbol: "SPCX",             fetchUrl: "" },
    { label: "TSLA/SPCX",        symbol: "TSLA/SPCX",        fetchUrl: "" },
  ];

const CHART_SYMBOLS = ["BTC/USDT", "ETH/USDT", "MSTR", "TSLA", "TLT", "VIX", "ASST", "SATA", "STRC", "SOL/USDT", "SPCX", "TSLA/SPCX"];

interface TickerItem {
  label: string;
  symbol: string;
  price: string;
  change: string;
  positive: boolean;
}

interface Props {
  selected: string;
  onSelect: (s: string) => void;
}

export default function TickerTape({ selected, onSelect }: Props) {
  const [tickers, setTickers] = useState<TickerItem[]>([]);
  const trackRef = useRef<HTMLDivElement>(null);

  // Fetch stock price from Yahoo Finance
  const fetchStockPrice = async (symbol: string): Promise<{ price: number; change: number } | null> => {
    try {
      const res = await fetch(
        `https://query1.finance.yahoo.com/v8/finance/chart/${symbol}?interval=1d&range=2d`
      );
      const json = await res.json();
      const result = json?.chart?.result?.[0];
      if (!result) return null;
      
      const meta = result.meta;
      const currentPrice = meta.regularMarketPrice || 0;
      const previousClose = meta.chartPreviousClose || meta.previousClose || currentPrice;
      const change = ((currentPrice - previousClose) / previousClose) * 100;
      
      return { price: currentPrice, change };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      const results: TickerItem[] = await Promise.all(
        TICKERS.map(async (t) => {
          // Handle pair trades (e.g., TSLA/SPCX)
          if (t.symbol.includes("/")) {
            return { label: t.label, symbol: t.symbol, price: "PAIR", change: "—", positive: true };
          }

          // Handle stock/ETF tickers (MSTR, TSLA, TLT, VIX, ASST, SATA, STRC, SPCX) with Yahoo Finance
          if (t.symbol === "MSTR" || t.symbol === "TSLA" || t.symbol === "TLT" || t.symbol === "VIX" || t.symbol === "ASST" || t.symbol === "SATA" || t.symbol === "STRC" || t.symbol === "SPCX") {
            const stockData = await fetchStockPrice(t.symbol);
            if (stockData) {
              return {
                label: t.label,
                symbol: t.symbol,
                price: stockData.price >= 1000
                  ? stockData.price.toLocaleString(undefined, { maximumFractionDigits: 0 })
                  : stockData.price.toFixed(2),
                change: (stockData.change >= 0 ? "+" : "") + stockData.change.toFixed(2) + "%",
                positive: stockData.change >= 0,
              };
            }
            return { label: t.label, symbol: t.symbol, price: "—", change: "—", positive: true };
          }
          
          // Handle crypto tickers with Binance
          if (!t.fetchUrl) {
            return { label: t.label, symbol: t.symbol, price: "—", change: "—", positive: true };
          }
          try {
            const res = await fetch(t.fetchUrl);
            const json = await res.json();
            const price = parseFloat(json.lastPrice);
            const change = parseFloat(json.priceChangePercent);
            return {
              label: t.label,
              symbol: t.symbol,
              price: price >= 1000
                ? price.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : price.toFixed(3),
              change: (change >= 0 ? "+" : "") + change.toFixed(2) + "%",
              positive: change >= 0,
            };
          } catch {
            return { label: t.label, symbol: t.symbol, price: "—", change: "—", positive: true };
          }
        })
      );
      setTickers(results);
    };

    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  // Duplicate items for seamless loop
  const items = [...tickers, ...tickers];

  return (
    <div className="w-full bg-gray-900 border-b border-gray-700 overflow-hidden relative" style={{ height: 40 }}>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          animation: ticker-scroll 40s linear infinite;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
      `}</style>
      <div ref={trackRef} className="ticker-track h-full items-center flex">
        {items.map((t, i) => (
          <button
            key={i}
            onClick={() => CHART_SYMBOLS.includes(t.symbol) && onSelect(t.symbol)}
            className={`flex items-center gap-2 px-5 h-full whitespace-nowrap transition-colors duration-150 ${
              CHART_SYMBOLS.includes(t.symbol)
                ? selected === t.symbol
                  ? "bg-blue-700 cursor-pointer"
                  : "hover:bg-gray-700 cursor-pointer"
                : "cursor-default"
            }`}
          >
            <span className="text-white text-sm font-semibold">{t.label}</span>
            <span className="text-gray-300 text-sm">${t.price}</span>
            <span className={`text-xs font-medium ${t.positive ? "text-green-400" : "text-red-400"}`}>
              {t.change}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
